import DOSSIER_GUIDE from './support/dossier_customer_help.json';
import ASSET_GUIDE from './support/asset_customer_help.json';
import RESEARCH_GUIDE from './research-home/research_customer_help.json';
import DEALFLOW_GUIDE from './dealflow/dealflow_customer_help.json';
import { useState, useEffect, useRef } from "react";
import { useProductGuideAvailable, prepareProductGuideOpening } from './assistant/product-guide-access';
import './assistant/product-guide-help.css';
import {createPortal} from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Mic, HelpCircle,
  X,
  BookOpen,
  MessageSquare,
  Lightbulb,
  ChevronRight,
  MapPin,
  RotateCcw,
  ArrowLeftRight,
} from "lucide-react";
import { API } from "@/App";
import { restartOnboardingTour, startPageTour } from "./OnboardingTour";
import {MONITOR_HELP_CONTENT,MONITOR_HELP_TIPS} from './monitor/monitorHelp';
import {MARKETS_HELP_CONTENT} from './markets/marketsHelp';
import {OVERVIEW_HELP_CONTENT,OVERVIEW_HELP_TIPS,normalizePageHelp} from "./overview/overviewHelp";
import {requestOverviewFeed} from "./overview/useOverviewFeeds";

/**
 * PageConcierge - Phase 120: Updated Contextual Help Drawer
 * 
 * A slide-out side drawer (360px) triggered by "?" icon.
 * Detects current route and displays relevant help content.
 * Content pulled from page_content MongoDB collection.
 * 
 * Phase 120 Route updates:
 * - /overview -> Overview (Command Center)
 * - /research -> Research (Company & Asset intelligence)
 * - /markets -> Markets (resolved scopes and explicit fresh reports)
 * - /dealflow -> Dealflow (formerly Radar — Form D filings)
 * - /monitor -> Monitor (News & watchlist)
 * - /company/:ticker -> Company Dossier
 * - /admin -> Admin Panel guide
 * - /support -> Support Hub guide
 */

// Default content when DB content not available
const DEFAULT_CONTENT = {
  overview: OVERVIEW_HELP_CONTENT,
  research: RESEARCH_GUIDE,
  dossier: DOSSIER_GUIDE,
  asset: ASSET_GUIDE,
  "market-intel": MARKETS_HELP_CONTENT,
  radar: DEALFLOW_GUIDE,
  monitor: MONITOR_HELP_CONTENT,
  wire: MONITOR_HELP_CONTENT,
  admin: {
    page_title: "Admin Panel",
    quick_guide: {
      title: "Organization Management",
      content: `The Admin Panel provides super admin controls for:

**User Management**: View all users, auth methods, and activity.

**Organization Controls**: Manage team subscriptions and permissions.

**Broadcasts**: Send global announcements to all users.

**Content CMS**: Edit Page Concierge and Knowledge Base content.`
    },
    key_metrics: [
      { term: "Team ROI", definition: "Aggregated value generated by organization usage." },
      { term: "Usage Rate", definition: "Rate of feature consumption across the team." }
    ],
    tip: "Use the Content tab to update help text without code deployments."
  },
  support: {
    page_title: "Support Hub",
    quick_guide: {
      title: "Getting Help",
      content: `The Support Hub provides multiple ways to get assistance:

**Knowledge Base**: Searchable articles on features and methodology.

**Messages**: Choose a category and send a private message. Open Messages to read replies.

**Email notifications**: Open the linked conversation to reply. Email replies do not synchronize into Messages.

**Keyboard Shortcuts**: Press ⌘K (Mac) or Ctrl+K (Windows) to open the command palette.`
    },
    key_metrics: [],
    tip: "Search by the task you want to complete, or open the relevant module guide. If you still need help, send a message with the page and the result you expected."
  },
  shortcuts: {
    page_title: "Keyboard Shortcuts",
    quick_guide: {
      title: "Quick Navigation",
      content: `**Command Palette**: ⌘K on Mac or Ctrl+K on Windows.
Open the command palette to navigate or search.

**Page help**: Choose Help on the current page for its guide and available tour.

**Close a dialog**: Use its close control or Escape. Your current page remains available behind it.`
    },
    key_metrics: [
      { term: "⌘K / Ctrl+K", definition: "Open command palette for quick actions" },
      { term: "Page Help", definition: "Open the current page guide with its Help control" },
      { term: "Escape", definition: "Close any open modal or palette" }
    ],
    tip: "Use the command palette to find a destination, then check the selected identity or scope before opening it."
  }
};

export function PageConciergeButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-8 h-8 flex items-center justify-center rounded-sm bg-[#059669]/10 hover:bg-[#059669]/20 transition-colors"
      title="Help & Guide"
      aria-label="Open page help"
      data-testid="page-concierge-btn"
    >
      <HelpCircle size={18} className="text-[#059669]" />
    </button>
  );
}

export function PageConciergeDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const guideAvailable = useProductGuideAvailable(location.pathname);
  const openGuide = () => {
    const open = prepareProductGuideOpening(location.pathname);
    if (!open) return;
    onClose();
    // Let Help unmount and release its focus trap/inert background first.
    requestAnimationFrame(open);
  };
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsNew, setWhatsNew] = useState([]);
  const [contentContext,setContentContext] = useState(null);
  const drawerRef=useRef(null);
  const closeRef=useRef(onClose);
  closeRef.current=onClose;

  // Default What's New items — repurposed as evergreen Tips & Tricks per page
  const DEFAULT_TIPS_BY_CONTEXT = {
    overview: OVERVIEW_HELP_TIPS,
    research: RESEARCH_GUIDE.tips_and_tricks,
    dossier: DOSSIER_GUIDE.tips_and_tricks,
    asset: ASSET_GUIDE.tips_and_tricks,
    "market-intel": [
      {title:"Confirm a scope and run fresh",description:"Search prepares a supported scope. Run fresh report collects current evidence; saved runs keep their original dates."},
      {title:"Expand the evidence",description:"Use the asset table or stage board to inspect linked studies and participant roles."},
      {title:"Take the same version with you",description:"AIRPack contains the displayed snapshot’s original evidence, normalized tables and source manifest."},
    ],
    radar: DEALFLOW_GUIDE.tips_and_tricks,
    monitor: MONITOR_HELP_TIPS,
    wire: MONITOR_HELP_TIPS,
  };

  // Determine page context from route
  const getPageContext = () => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return "admin";
    if (path.startsWith("/dealflow") || path.startsWith("/private-markets") || path.startsWith("/radar")) return "radar";
    if (path.startsWith("/markets") || path.startsWith("/landscape") || path.startsWith("/strategy")) return "market-intel";
    if (path.startsWith("/monitor") || path.startsWith("/wire")) return "monitor";
    if (path.startsWith("/overview") || path.startsWith("/dashboard") || path.startsWith("/home")) return "overview";
    if (path.startsWith("/asset/")) return "asset";
    if (path.startsWith("/company/")) return "dossier";
    if (path.startsWith("/research") || path.startsWith("/scout")) return "research";
    if (path.startsWith("/support")) return "support";
    return "overview";
  };

  const pageContext = getPageContext();

  const DEFAULT_WHATS_NEW = DEFAULT_TIPS_BY_CONTEXT[pageContext] || DEFAULT_TIPS_BY_CONTEXT.overview;
  const isLandscapePage = pageContext === "market-intel";

  useEffect(() => {
    if(!isOpen) return;
    const controller=new AbortController();let active=true;
    const fallback=normalizePageHelp(null,pageContext,DEFAULT_CONTENT[pageContext],DEFAULT_WHATS_NEW);
    setLoading(false);setContent(fallback.content);setWhatsNew(fallback.tips);setContentContext(pageContext);
    requestOverviewFeed(`${API}/support/page-content/${pageContext}`,{signal:controller.signal})
      .then(data=>{if(active){const result=normalizePageHelp(data,pageContext,DEFAULT_CONTENT[pageContext],DEFAULT_WHATS_NEW);setContent(result.content);setWhatsNew(result.tips);}})
      .catch(()=>{/* Reviewed local guide remains usable when its saved override fails. */})
      .finally(()=>{if(active)setLoading(false);});
    return ()=>{active=false;controller.abort();};
  },[isOpen,pageContext,isLandscapePage]);

  useEffect(() => {
    if(!isOpen || !drawerRef.current) return;
    const dialog=drawerRef.current,previous=document.activeElement;
    const background=Array.from(document.body.children).filter(node=>!node.contains(dialog) && node.getAttribute('data-testid')!=='page-help-backdrop');
    const states=background.map(node=>[node,node.inert]);background.forEach(node=>{node.inert=true;});
    dialog.querySelector('[data-testid="page-help-close"]')?.focus();
    const key=event=>{
      if(event.key==='Escape'){event.preventDefault();closeRef.current();return;}
      if(event.key!=='Tab')return;
      const nodes=Array.from(dialog.querySelectorAll('button:not([disabled]),a[href],[tabindex="0"]'));
      const first=nodes[0],last=nodes[nodes.length-1];
      if(!first){event.preventDefault();dialog.focus();return;}
      if(event.shiftKey && (document.activeElement===first || !dialog.contains(document.activeElement))){event.preventDefault();last.focus();}
      else if(!event.shiftKey && (document.activeElement===last || !dialog.contains(document.activeElement))){event.preventDefault();first.focus();}
    };
    document.addEventListener('keydown',key,true);
    return ()=>{document.removeEventListener('keydown',key,true);states.forEach(([node,inert])=>{node.inert=inert;});if(previous?.isConnected)previous.focus?.();};
  },[isOpen]);

  if (!isOpen) return null;

  const displayContent = (contentContext === pageContext && content) || DEFAULT_CONTENT[pageContext];
  const displayTips = whatsNew;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/50 z-[1000] transition-opacity"
        data-testid="page-help-backdrop"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef} role="dialog" aria-modal="true" aria-labelledby="page-help-title" tabIndex={-1}
        data-testid="page-help-drawer"
        className="fixed right-0 top-0 h-dvh w-[360px] max-w-full bg-white border-l border-slate-200 shadow-sm z-[1001] overflow-y-auto"
        style={{
          animation: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? "none" : "slideInRight 200ms ease-out"
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HelpCircle size={20} className="text-[#059669]" />
            <span id="page-help-title" className="font-semibold text-slate-900">Page Help</span>
          </div>
          <button
            onClick={onClose} aria-label="Close page help" data-testid="page-help-close"
            className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {guideAvailable && <section className="bds-guide-help-card" aria-labelledby="page-help-guide-title" data-testid="page-help-product-guide">
            <span className="bds-guide-help-icon" aria-hidden="true"><Mic size={18} /></span>
            <div>
              <h3 id="page-help-guide-title">Need help with this page?</h3>
              <p>Ask Product Guide how to use this module.</p>
              <button type="button" onClick={openGuide} data-testid="page-help-open-guide"><Mic size={15} aria-hidden="true" />Open Product Guide</button>
              <small>Choose voice or text. Start when you’re ready.</small>
            </div>
          </section>}
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-slate-200 rounded w-1/2" />
              <div className="h-20 bg-slate-100 rounded" />
              <div className="h-16 bg-slate-100 rounded" />
            </div>
          ) : (
            <>
              {/* Tips & Tricks — hidden on Markets page */}
              {displayTips && displayTips.length > 0 && !isLandscapePage && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb size={16} className="text-blue-600" />
                    <h3 className="font-semibold text-slate-900 text-sm">Tips &amp; Tricks</h3>
                  </div>
                  <div className="space-y-2.5">
                    {displayTips.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium text-slate-900">{item.title}</span>
                        <span className="text-slate-500 ml-1">— {item.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Page Indicator */}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin size={14} />
                <span>You&apos;re on: <strong className="text-slate-900">{displayContent.page_title}</strong></span>
              </div>

              {/* Quick Guide Section */}
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen size={16} className="text-[#059669]" />
                  <h3 className="font-semibold text-slate-900 text-sm">
                    {displayContent.quick_guide?.title || "Quick Guide"}
                  </h3>
                </div>
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                  {displayContent.quick_guide?.content?.split("**").map((part, i) => 
                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                  )}
                </div>
              </div>

              {/* Markets vs. Research disambiguation — Markets page only */}
              {isLandscapePage && (
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                    <ArrowLeftRight size={14} className="text-slate-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Markets vs. Research</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    <div className="px-4 py-3 flex items-start gap-3">
                      <span className="mt-0.5 text-[10px] font-bold text-[#059669] bg-[#059669]/10 px-1.5 py-0.5 rounded shrink-0">MARKETS</span>
                      <p className="text-sm text-slate-700">Therapeutic landscape — supported treatments, study programs and participant roles within a defined, dated scope.</p>
                    </div>
                    <div className="px-4 py-3 flex items-start gap-3">
                      <span className="mt-0.5 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">RESEARCH</span>
                      <p className="text-sm text-slate-700">Single entity view — one company's financials, pipeline &amp; runway, or one drug's retrieved trial history.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Key Metrics Section */}
              {displayContent.key_metrics && displayContent.key_metrics.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                    <ChevronRight size={14} className="text-[#059669]" />
                    Key Metrics Explained
                  </h4>
                  <div className="space-y-2">
                    {displayContent.key_metrics.map((metric, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium text-slate-900">{metric.term}:</span>
                        <span className="text-slate-600 ml-1">{metric.definition}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tip Box */}
              {displayContent.tip && (
                <div className="bg-blue-50 border border-blue-100 rounded-sm p-4">
                  <div className="flex items-start gap-2">
                    <Lightbulb size={16} className="text-[#059669] shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">{displayContent.tip}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer CTAs */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 space-y-2">
          <Button
            onClick={() => {
              onClose();
              if (pageContext === "overview") {
                setTimeout(()=>startPageTour("dashboard"),0);
              } else if (pageContext === "monitor") {
                setTimeout(()=>startPageTour("wire"),0);
              } else if (isLandscapePage) {
                setTimeout(()=>startPageTour("landscape"),0);
              } else {
                restartOnboardingTour();
              }
            }}
            variant="outline"
            className="w-full justify-start gap-2 h-10 text-slate-700 border-slate-200 hover:bg-slate-50"
            data-testid="retake-tour-btn"
          >
            <RotateCcw size={16} />
            {pageContext === "overview" ? "Take Overview tour" : isLandscapePage ? "Retake Markets Tour" : pageContext === "monitor" ? "Take Monitor tour" : "Retake Platform Tour"}
          </Button>
          <Button
            onClick={() => {
              onClose();
              navigate("/support");
            }}
            variant="outline"
            className="w-full justify-start gap-2 h-10 text-slate-700 border-slate-200 hover:bg-slate-50"
          >
            <BookOpen size={16} />
            View Knowledge Base
          </Button>
          <Button onClick={() => { onClose(); navigate("/messages?compose=1"); }} variant="outline" className="w-full justify-start gap-2 min-h-11 text-slate-700 border-slate-200 hover:bg-slate-50"><MessageSquare size={16} />Message the team</Button>
          <Button onClick={() => { onClose(); navigate("/messages"); }} variant="outline" className="w-full justify-start gap-2 min-h-11 text-slate-700 border-slate-200 hover:bg-slate-50"><MessageSquare size={16} />View messages</Button>
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>, document.body
  );
}

export default PageConciergeDrawer;
